import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { User, ActivityLog, GPSLocation, Permissions, AppNotification } from '../types';
import { supabaseAdmin } from '../lib/supabase';
const NOTIFS_STORAGE_KEY = 'ventastrack_app_notifications';
function loadStoredNotifications(): AppNotification[] {
  try {
    const stored = localStorage.getItem(NOTIFS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map((n: any) => ({
        ...n,
        timestamp: new Date(n.timestamp)
      }));
    }
  } catch {}
  return [];
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  availableUsers: User[];
  login: (emailOrId: string, password?: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  logout: () => void;
  activityLogs: ActivityLog[];
  addActivityLog: (action: string, details: string) => void;
  notifications: AppNotification[];
  addNotification: (type: AppNotification['type'], title: string, message: string) => void;
  markNotificationsRead: () => void;
  isLoading: boolean;
  refreshUsers: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Helper para mapear perfil de DB a tipo User
function mapProfile(row: any, roleMap: Record<string, any>): User {
  const role = roleMap[row.rol_id] || {
    nombre: 'Desconocido',
    permisos: {}
  };
  return {
    id: row.id,
    nombre: row.nombre,
    email: row.email || '',
    password: row.password,
    rolId: row.rol_id,
    rolNombre: role.nombre,
    permisos: role.permisos as Permissions,
    activo: row.activo,
    fechaCreacion: new Date(row.fecha_creacion)
  };
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>(loadStoredNotifications);
  const [isLoading, setIsLoading] = useState(true);
  // ── Inactivity timeout: 50 minutes ────────────────────────────────────────
  const INACTIVITY_TIMEOUT_MS = 50 * 60 * 1000; // 50 minutes
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      // Auto-logout on inactivity
      setUser(null);
      localStorage.removeItem('ventastrack_user_id');
      window.location.reload();
    }, INACTIVITY_TIMEOUT_MS);
  }, []);
  useEffect(() => {
    if (!user) {
      // Not authenticated — clear timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }
    // Start tracking inactivity
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'mousedown'];
    const handleActivity = () => resetInactivityTimer();
    activityEvents.forEach(event => window.addEventListener(event, handleActivity, {
      passive: true
    }));
    // Start initial timer
    resetInactivityTimer();
    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer]);
  const getCurrentLocation = (): Promise<GPSLocation | null> => {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      // Safety timeout in case geolocation hangs
      const timeout = setTimeout(() => resolve(null), 5000);
      navigator.geolocation.getCurrentPosition(pos => {
        clearTimeout(timeout);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          timestamp: new Date()
        });
      }, () => {
        clearTimeout(timeout);
        resolve(null);
      }, {
        enableHighAccuracy: false,
        timeout: 4000,
        maximumAge: 60000
      });
    });
  };
  // Cargar usuarios y roles
  const loadUsersAndRoles = async () => {
    try {
      // 1. Cargar roles
      const {
        data: rolesData,
        error: rolesError
      } = await supabaseAdmin.from('roles').select('*');
      if (rolesError) throw rolesError;
      const roleMap: Record<string, any> = {};
      rolesData?.forEach(r => {
        roleMap[r.id] = r;
      });
      // 2. Cargar perfiles
      const {
        data: profilesData,
        error: profilesError
      } = await supabaseAdmin.from('profiles').select('*').eq('activo', true).order('nombre');
      if (profilesError) throw profilesError;
      if (profilesData) {
        const mappedUsers = profilesData.map(p => mapProfile(p, roleMap));
        setAvailableUsers(mappedUsers);
      }
    } catch (err) {
      console.error('Error cargando usuarios/roles:', err);
      // Fallback básico si falla DB
      setAvailableUsers([]);
    }
  };
  const loadActivityLogs = async (userId: string, isAdmin: boolean) => {
    try {
      let query = supabaseAdmin.from('activity_logs').select('*').order('timestamp', {
        ascending: false
      }).limit(500);
      if (!isAdmin) query = query.eq('user_id', userId);
      const {
        data
      } = await query;
      if (data) {
        setActivityLogs(data.map(log => ({
          id: log.id,
          userId: log.user_id,
          userName: log.user_name,
          action: log.action,
          timestamp: new Date(log.timestamp),
          location: log.location ? {
            ...log.location,
            timestamp: new Date(log.location.timestamp)
          } : null,
          details: log.details
        })));
      }
    } catch (err) {
      console.error('Error loading logs:', err);
    }
  };
  useEffect(() => {
    // Safety timeout: if init hangs for more than 8 seconds, force loading to stop
    const safetyTimer = setTimeout(() => {
      console.warn('[Auth] Safety timeout — forcing loading to stop');
      setIsLoading(false);
    }, 8000);
    const init = async () => {
      try {
        await loadUsersAndRoles();
        // Intentar restaurar sesión
        const savedUserId = localStorage.getItem('ventastrack_user_id');
        if (savedUserId) {
          const {
            data: roleData
          } = await supabaseAdmin.from('roles').select('*');
          const roleMap: Record<string, any> = {};
          roleData?.forEach(r => roleMap[r.id] = r);
          const {
            data: userData
          } = await supabaseAdmin.from('profiles').select('*').eq('id', savedUserId).single();
          if (userData && userData.activo) {
            const userObj = mapProfile(userData, roleMap);
            setUser(userObj);
            if (userData.avatar_url) {
              localStorage.setItem(`ventastrack_avatar_${userObj.id}`, userData.avatar_url);
            }
            window.dispatchEvent(new Event('avatar-updated'));
            // Load activity logs but don't block on it
            loadActivityLogs(userObj.id, userObj.rolNombre === 'Administrador').catch(() => {});
          }
        }
      } catch (err) {
        console.error('Error inicializando sesión:', err);
      } finally {
        clearTimeout(safetyTimer);
        setIsLoading(false);
      }
    };
    init();
    return () => clearTimeout(safetyTimer);
  }, []);
  // ── Auto-refresh user permissions via event from AppContext ──────────────
  // AppContext already subscribes to roles/profiles changes via realtime.
  // We listen for the 'supabase-change' custom event to avoid duplicate channels.
  useEffect(() => {
    if (!user) return;
    const refreshCurrentUserPermissions = async () => {
      try {
        const {
          data: roleData
        } = await supabaseAdmin.from('roles').select('*').eq('id', user.rolId).single();
        if (roleData) {
          const newPermisos = roleData.permisos as Permissions;
          const currentStr = JSON.stringify(user.permisos);
          const newStr = JSON.stringify(newPermisos);
          if (currentStr !== newStr) {
            setUser(prev => prev ? {
              ...prev,
              permisos: newPermisos,
              rolNombre: roleData.nombre
            } : null);
          }
        }
      } catch (err) {
        console.error('Error refreshing user permissions:', err);
      }
    };
    // Listen for realtime changes dispatched by AppContext's roles/profiles channels
    const handleChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.table === 'roles' || detail?.table === 'profiles') {
        refreshCurrentUserPermissions();
      }
    };
    window.addEventListener('supabase-change', handleChange);
    return () => {
      window.removeEventListener('supabase-change', handleChange);
    };
  }, [user?.id, user?.rolId]);
  const login = useCallback(async (emailOrId: string, password?: string): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      // Determinar si es email o ID
      const isEmail = emailOrId.includes('@');
      let userData: any = null;
      let error: any = null;
      if (isEmail) {
        // Buscar por email
        const res = await supabaseAdmin.from('profiles').select('*').eq('email', emailOrId.trim().toLowerCase()).single();
        userData = res.data;
        error = res.error;
      } else {
        // Buscar por ID (para restaurar sesión)
        const res = await supabaseAdmin.from('profiles').select('*').eq('id', emailOrId).single();
        userData = res.data;
        error = res.error;
      }
      if (error || !userData) {
        return {
          success: false,
          error: 'Correo electrónico no encontrado'
        };
      }
      if (!userData.activo) {
        return {
          success: false,
          error: 'Usuario desactivado'
        };
      }
      if (userData.password && userData.password !== password) {
        return {
          success: false,
          error: 'Contraseña incorrecta'
        };
      }
      const {
        data: roleData
      } = await supabaseAdmin.from('roles').select('*').eq('id', userData.rol_id).single();
      const roleMap = {
        [userData.rol_id]: roleData
      };
      const userObj = mapProfile(userData, roleMap);
      setUser(userObj);
      localStorage.setItem('ventastrack_user_id', userObj.id);
      // Sync avatar from DB to localStorage so Header shows it immediately
      if (userData.avatar_url) {
        localStorage.setItem(`ventastrack_avatar_${userObj.id}`, userData.avatar_url);
      }
      window.dispatchEvent(new Event('avatar-updated'));
      (async () => {
        try {
          const location = await getCurrentLocation();
          await supabaseAdmin.from('activity_logs').insert({
            user_id: userObj.id,
            user_name: userObj.nombre,
            action: 'Inicio de sesión',
            location: location ? {
              lat: location.lat,
              lng: location.lng,
              timestamp: location.timestamp.toISOString()
            } : null,
            details: 'Acceso con correo y contraseña'
          });
          loadActivityLogs(userObj.id, userObj.rolNombre === 'Administrador').catch(() => {});
        } catch (_) {}
      })();
      return {
        success: true
      };
    } catch (err: any) {
      return {
        success: false,
        error: 'Error de conexión'
      };
    }
  }, []);
  const logout = useCallback(async () => {
    if (user) {
      const location = await getCurrentLocation();
      await supabaseAdmin.from('activity_logs').insert({
        user_id: user.id,
        user_name: user.nombre,
        action: 'Cierre de sesión',
        location: location ? {
          lat: location.lat,
          lng: location.lng,
          timestamp: location.timestamp.toISOString()
        } : null,
        details: 'Salida del sistema'
      });
    }
    setUser(null);
    localStorage.removeItem('ventastrack_user_id');
  }, [user]);
  const addActivityLog = useCallback(async (action: string, details: string) => {
    if (!user) return;
    const location = await getCurrentLocation();
    const {
      data
    } = await supabaseAdmin.from('activity_logs').insert({
      user_id: user.id,
      user_name: user.nombre,
      action,
      location: location ? {
        lat: location.lat,
        lng: location.lng,
        timestamp: location.timestamp.toISOString()
      } : null,
      details
    }).select().single();
    if (data) {
      setActivityLogs(prev => [{
        id: data.id,
        userId: data.user_id,
        userName: data.user_name,
        action: data.action,
        timestamp: new Date(data.timestamp),
        location: data.location ? {
          ...data.location,
          timestamp: new Date(data.location.timestamp)
        } : null,
        details: data.details
      }, ...prev]);
    }
  }, [user]);
  // Activity logs refresh on page navigation / manual refresh — no realtime channel needed
  // This saves a database connection on the Supabase pool
  const addNotification = useCallback((type: AppNotification['type'], title: string, message: string) => {
    const notif: AppNotification = {
      id: `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type,
      title,
      message,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => {
      const updated = [notif, ...prev].slice(0, 50);
      localStorage.setItem(NOTIFS_STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifications-updated'));
      return updated;
    });
  }, []);
  const markNotificationsRead = useCallback(() => {
    setNotifications(prev => {
      const updated = prev.map(n => ({
        ...n,
        read: true
      }));
      localStorage.setItem(NOTIFS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    availableUsers,
    login,
    logout,
    activityLogs,
    addActivityLog,
    notifications,
    addNotification,
    markNotificationsRead,
    isLoading,
    refreshUsers: loadUsersAndRoles
  }} data-id="element-297">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}